import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { forkJoin, Subscription, tap } from 'rxjs';
import { MediaMatcher } from '@angular/cdk/layout';
import { MatDialog } from '@angular/material/dialog';
import { InfoDialogComponent }  from '@trivelope/triv-ui-tools-lib';
import { MemberService } from 'src/app/services/member.service';
import { SectionService } from 'src/app/services/section.service';
import { DataService } from 'src/app/services/data.service';
import { Member } from 'src/app/classes/member';
import { Section } from 'src/app/classes/section';
import { ReportingYear, ReportingYearData } from 'src/app/classes/reportingData';
import { YearService } from 'src/app/services/year.service';
import { formatNumber } from '@angular/common';
import { Environment } from 'src/app/classes/environment';
import { FormControl } from '@angular/forms';


@Component({
	selector: 'app-statistics',
	templateUrl: './statistics.component.html',
	styleUrls: [ './statistics.component.css' ]
})
export class StatisticsComponent implements OnInit, OnDestroy {


	subscriptions = new Subscription();
	bInProgress = true;

	members: Member[] = [];
	memberListCombobox: { id: string, label: string }[] = [];
	// membersToChoose = [ 123456 ];	// random numbers are used as identifiers
	selectedMembers: Member[] = [];

	sections: Section[] = [];
	sectionsAssigned: Section[] = [];
	selectedSectionIds: string[] = [];
	selectedSectionsCtrl = new FormControl( [ "" ] );

	reportingYears: ReportingYear[] = [];
	years: string[] = [];
	yearSelected = '2020';

	// chipsAutocompleteElements: ChipsAutocompleteElem[] = [];

	columnsStatsDisplayed: string[] = [];
	dataSources: MatTableDataSource<any>[] = [];

	_mobileQueryListener = (e: any) => {};
	_printQueryListener = (e: any) => {};
	// lt-md 	'screen and (max-width: 959px)'
    mobileQuery: MediaQueryList = this.media.matchMedia( 'screen and (max-width: 959px)' );
    printQuery: MediaQueryList = this.media.matchMedia( 'print' );

	colMax = 2;
	indexColLeft = 0;
	columnsStats: string[] = [];

	// twigsNotEuros = [ 'anzahlSchaedenBj', 'anzahlVertraegeBj' ];

	bShowAutoComplete = true;
	bShowBtnCompare = false;


	constructor(
		private media: MediaMatcher,
		private matDialog: MatDialog,
		private memberService: MemberService,
		private sectionService: SectionService,
		private dataService: DataService,
		private yearService: YearService,
		@Inject('environment') private environment: Environment,
	) {}


	ngOnInit(): void {

		this.subscriptions.add(
			this.selectedSectionsCtrl.valueChanges.subscribe( arr => {

				if( !arr ) return;
				this.selectedSectionIds = arr;
				if( this.selectedSectionIds.includes('') ) {
					this.selectedSectionsCtrl.patchValue( [ '' ], { emitEvent: false } );
					this.selectedSectionIds = [ "" ];
				}
				this.prepareData();

			})
		);

		this.colMax = ( this.mobileQuery.matches ) ? 2 : 5;

        this._mobileQueryListener = (mobileQuery) => {
			console.log( 'mobile?', mobileQuery, this.mobileQuery.matches, this.mobileQuery.media )

			this.colMax = ( this.mobileQuery.matches ) ? 2 : 5;
			this.indexColLeft = 0;
			this.defineColumnsToBeDisplayed();
		};
		this.mobileQuery.addEventListener( 'change', this._mobileQueryListener );

		this._printQueryListener = (printQuery) => {

			console.log( 'printQuery?', printQuery, this.printQuery.matches, this.printQuery.media )
			if( this.printQuery.matches ) {
				this.colMax = 5;
				this.indexColLeft = 0;
				this.defineColumnsToBeDisplayed();
			}

		};
		this.printQuery.addEventListener( 'change', this._printQueryListener );



		forkJoin( [

			this.memberService.getAllExceptPrivate().pipe(
				tap( (members: Member[]) => {
					this.members = members;
					this.refreshMemberListCombobox();
				})
			),
			this.sectionService.getAll().pipe(
				tap( (sections: Section[]) => {
					this.sections = sections;
				} )
			),
			this.yearService.getAll().pipe(
				tap( (reportingYears: ReportingYear[]) => {
					this.reportingYears = reportingYears;
					for( let ry of reportingYears ) {
						// the year needs to be displayed even if it's not released anymore
						// if( !ry.bjFreigabe ) continue;
						if( !ry.bjSichtbar ) continue;
						this.years.push( ry.bjJahr );
					}
					this.years.sort( (a,b) => { return b.localeCompare(a); } );
					if( this.years.length ) this.yearSelected = this.years[0];
				})
			)

		] ).subscribe( () => {
			this.bInProgress = false;
		});

	}

	ngOnDestroy() {
		this.mobileQuery.removeEventListener('change', this._mobileQueryListener);
		this.subscriptions.unsubscribe();
	}


	selectMember( memberId: string|null ) {

		// console.log( 'memberSelected', memberId )
		if( !memberId ) return;
		const member = this.members.find( m => m.mitgliedId === memberId );
		if( !member ) return;
		if( this.selectedMembers.includes( member ) ) return;

		this.selectedMembers.push( member );

		this.refreshMemberListCombobox();
		this.memberSelectionChanged();

		this.bShowBtnCompare = true;
		this.bShowAutoComplete = false;

	}

	removeMember( memberId: string ) {
		// console.log( 'removeMember', memberId )
		const member = this.members.find( m => m.mitgliedId === memberId );
		// console.log( 'member', member )
		if( !member ) return;
		if( !this.selectedMembers.includes( member ) ) return;

		const nPos = this.selectedMembers.findIndex( m => m.mitgliedId === member.mitgliedId );
		// console.log( 'nPos', nPos )
		if( nPos < 0 ) return;

		this.selectedMembers.splice( nPos, 1 );

		this.refreshMemberListCombobox();
		this.memberSelectionChanged();

		if( !this.selectedMembers.length ) {
			this.bShowBtnCompare = false;
			this.bShowAutoComplete = true;
		}
	}

	showCompareInput() {
		this.bShowBtnCompare = false;
		this.bShowAutoComplete = true;
	}

	private refreshMemberListCombobox() {
		this.memberListCombobox = [];
		for( const m of this.members ) {
			if( this.selectedMembers.find( member => member.mitgliedId === m.mitgliedId ) ) continue;

			this.memberListCombobox.push( { id: m.mitgliedId, label: m.mitgliedsname } );
		}
	}


	private memberSelectionChanged() {

		this.bInProgress = true;
		this.sectionsAssigned = [];

		const selectedMembersId = this.selectedMembers.map( m => m.mitgliedId );
		this.memberService.getHistoricalSectionsOfMembers( selectedMembersId ).subscribe( sections => {

			this.sectionsAssigned = sections
				.sort( (a,b) => a.sparteBezeichnung.localeCompare( b.sparteBezeichnung ) );

			if( !this.selectedSectionIds.length || this.selectedSectionIds.includes('') ) {
				this.selectedSectionIds = [ '' ];
			}
			else if( this.sectionsAssigned.length == 1 ) {
				this.selectedSectionIds = [ this.sectionsAssigned[0].sparteId ];
			}
			else {

				const validIds = this.selectedSectionIds
					.filter( id => !!this.sectionsAssigned.find( s => s.sparteId == id ) );
				if( validIds.length ) {
					this.selectedSectionIds = validIds;
				}
				else {
					this.selectedSectionIds = [ '' ];
				}

			}
			this.selectedSectionsCtrl.patchValue( this.selectedSectionIds, { emitEvent: false } );

			this.prepareData();

		});

	}

	prepareData() {

		this.columnsStats = [];
		this.columnsStatsDisplayed = [];
		this.dataSources = [];
		this.indexColLeft = 0;

		this.generateColumns();
		this.bInProgress = true;

		if( this.selectedMembers.length == 1 ) {

			this.dataService.getAllPublishedReportingYearsData( this.selectedMembers[0].mitgliedId ).subscribe( rydMap => {

				this.bInProgress = false;
				this.generateDataSourcesPerYear( rydMap );

			});

		}
		else if( this.selectedMembers.length > 1 ) {

			const ry = this.reportingYears.find( o => o.bjJahr === this.yearSelected );
			if( !ry ) {
				console.log( 'bug? year not valid', this.yearSelected, this.reportingYears );
				return;
			}

			const mitgliedIds = this.selectedMembers.map( m => m.mitgliedId );
			this.dataService.getComparisonData( this.yearSelected, mitgliedIds ).subscribe( ryls => {

				const dataMap = new Map<string, ReportingYearData[]>();
				for( const ryl of ryls ) {
					dataMap.set( ryl.mitgliedId, ryl.daten );
				}
				this.bInProgress = false;
				this.generateDataSourcesPerCompany( dataMap );

			});

		}
	}

	generateDataSourcesPerYear( dataMap: Map<string, ReportingYearData[]> ) {

		for( let section of this.sectionsAssigned ) {

			if(  !this.selectedSectionIds.includes( '' ) && !this.selectedSectionIds.includes( section.sparteId )  ) continue;

			const dataPerYear = [];
			for( let twig of this.environment.twigs ) {

				const d:{ [key:string] : string | number | boolean } = {};
				d.twigXS = twig.label;
				d.twigXL = twig.label;
				d.info = twig.info;

				for( let ry of this.reportingYears ) {

					const ryds = dataMap.get( ry.bjJahr );
					if( !ryds ) {
						d[ ry.bjJahr ] = '';
						continue;
					}
					const ryd = ryds.find( d => d.sparteId == section.sparteId );
					if( !ryd ) {
						d[ ry.bjJahr ] = '';
						continue;
					}
					let value = (<any>ryd)[ twig.id ];
					if( !value && value !== 0 ) value = '';
					d[ ry.bjJahr ] = value;
					// d[ m.mitgliedId ] = Math.round( 1000000 * Math.random() ) / 100 ;

				}

				// d.bCurrency = !this.twigsNotEuros.includes( twig.id );
				d.bCurrency = twig.currency;

				dataPerYear.push( d );

			}

			if( !dataPerYear.length ) {
				const d: { [key:string] : string } = {};
				d.twigXS = 'Keine Data für Sparte/Jahr verfügbar';
				d.twigXL = 'Keine Data für Sparte/Jahr verfügbar';
				for( let m of this.selectedMembers ) {
					d[ m.mitgliedId ] = '';
				}
				dataPerYear.push( d );
			}

			this.dataSources.push( new MatTableDataSource<any>( dataPerYear ) );

		}


		if( this.dataSources.length > 1 ) {

			const dataPerYearSum: { [key:string] : string | number }[] = [];

			for( let ds of this.dataSources ) {

				for( let i = 0; i < ds.data.length; i++ ) {

					const data = ds.data[i];
					if( !dataPerYearSum[i] ) dataPerYearSum[i] = {};

					for( let property in data ) {

						if( !data.hasOwnProperty(property) ) continue;
						const year = +property;
						if( !year ) dataPerYearSum[i][ property ] = data[ property ];
						else {

							if( data[ property ] === "" ) {
								if( dataPerYearSum[i][ property ] === undefined ) {
									dataPerYearSum[i][ property ] = "";
								}
							}
							else {
								if( !dataPerYearSum[i][ property ] ) dataPerYearSum[i][ property ] = 0;
								dataPerYearSum[i][ property ] = +dataPerYearSum[i][ property ] + data[ property ];
							}

						}
					}

				}

			}

			this.dataSources.push( new MatTableDataSource<any>( dataPerYearSum ) );

		}

	}


	generateDataSourcesPerCompany( dataMap: Map<string, ReportingYearData[]> ) {

		for( let section of this.sectionsAssigned ) {

			if(  !this.selectedSectionIds.includes( '' ) && !this.selectedSectionIds.includes( section.sparteId )  ) continue;

			let dataPerCompany = [];
			let bAtLeastOneData = false;
			for( let twig of this.environment.twigs ) {

				let d:any = {};
				d.twigXS = twig.label;
				d.twigXL = twig.label;
				d.info = twig.info;

				for( let m of this.selectedMembers ) {

					const ryds = dataMap.get( m.mitgliedId );
					if( !ryds ) {
						d[ m.mitgliedId ] = '';
						continue;
					}
					const ryd = ryds.find( d => d.sparteId == section.sparteId );
					if( !ryd ) {
						d[ m.mitgliedId ] = '';
						continue;
					}
					let value = (<any>ryd)[ twig.id ];
					if( !value && value !== 0 ) value = '';
					else bAtLeastOneData = true;
					d[ m.mitgliedId ] = value;
				}

				// d.bCurrency = !this.twigsNotEuros.includes( twig.id );
				d.bCurrency = twig.currency;
				dataPerCompany.push( d );
			}

			if( !bAtLeastOneData ) {
				dataPerCompany = [];
			}

			const ds = new MatTableDataSource<any>( dataPerCompany );
			this.dataSources.push( ds );
		}


		if( this.dataSources.length > 1 ) {

			const dataPerYearSum: { [key:string] : string | number }[] = [];

			for( let ds of this.dataSources ) {

				for( let i = 0; i < ds.data.length; i++ ) {

					const data = ds.data[i];
					if( !dataPerYearSum[i] ) dataPerYearSum[i] = {};

					for( let property in data ) {

						if( !data.hasOwnProperty(property) ) continue;

						const bMember = !!this.selectedMembers.find( m => m.mitgliedId == property );
						if( !bMember ) dataPerYearSum[i][ property ] = data[ property ];
						else {

							if( data[ property ] === "" ) {
								if( dataPerYearSum[i][ property ] === undefined ) {
									dataPerYearSum[i][ property ] = "";
								}
							}
							else {
								if( !dataPerYearSum[i][ property ] ) dataPerYearSum[i][ property ] = 0;
								dataPerYearSum[i][ property ] = +dataPerYearSum[i][ property ] + data[ property ];
							}

						}
					}

				}

			}

			this.dataSources.push( new MatTableDataSource<any>( dataPerYearSum ) );

		}

	}


	sectionDesignation( i: number ): string {

		if( (this.dataSources.length > 1) && (i == this.dataSources.length-1) ) return "Summe aller Sparten";

		if( !this.selectedSectionIds.includes( '' ) ) {

			const section = this.sections.find( s => s.sparteId == this.selectedSectionIds[i] );
			return section ? section.sparteBezeichnung : '';

		}
		else if( this.sectionsAssigned && this.sectionsAssigned.length && this.sectionsAssigned[i] ) {

			return this.sectionsAssigned[i].sparteBezeichnung;

		}

		return "-";

	}

	colDesignation( id: string ): string {

		if( this.selectedMembers.length == 1 ) return id;
		const member = this.members.find( m => m.mitgliedId == id );
		if( member ) return member.mitgliedsname;
		return '';

	}

	formatStatsValue( row: any, n: number ): string {
		if( !n && n !== 0 ) return "-";		// N/A
		return ( row.bCurrency )
			? "" + formatNumber( n, 'de-DE', '1.2' ) + ' €'
			: formatNumber( n, 'de-DE', '1.0-2' );
	}

	generateColumns() {
		const nbMembers = this.selectedMembers.length;
		this.columnsStats = [];
		if( nbMembers == 1 ) {
			for( let y of this.years ) {
				this.columnsStats.push( '' + y );
				// this.columnsStats.push( 'col' + this.columnsStats.length );
			}
		}
		else if( nbMembers > 1 ) {
			for( let m of this.selectedMembers ) {
				this.columnsStats.push( '' + m.mitgliedId  );
				// this.columnsStats.push( 'col' + this.columnsStats.length );
			}
		}
		this.defineColumnsToBeDisplayed();
	}

	defineColumnsToBeDisplayed() {
		this.columnsStatsDisplayed = [ 'twigXL' ];
		for( let i = this.indexColLeft; (i < this.indexColLeft + this.colMax) && (i < this.columnsStats.length) ; i++ ) {
			this.columnsStatsDisplayed.push( '' + this.columnsStats[i] );
		}
	}

	slideColumnsLeft() {
		console.log( 'slideLeft', this.indexColLeft )
		if( this.indexColLeft <= 0 ) return;

		this.indexColLeft--;
		this.defineColumnsToBeDisplayed();
	}
	slideColumnsRight() {
		console.log( 'slideRight', this.indexColLeft )
		if( this.indexColLeft > ( this.columnsStats.length - 1 - this.colMax ) ) return;

		this.indexColLeft++;
		this.defineColumnsToBeDisplayed();
	}
	preventDefault(event: MouseEvent) {
		// console.log( 'test', event )
		// to prevent text selection while clicking on an arrow
		event.preventDefault();
	}


	showInfoMember( i: number ) {
		const member = this.selectedMembers[i];
		const arrInfos = [ member.mitgliedsname ];

		let str = '';
		if( member.gsPLZ ) str += member.gsPLZ + ' ';
		if( member.gsOrt) str += member.gsOrt;
		arrInfos.push( str );

		if( member.gsTelefon ) arrInfos.push( member.gsTelefon );
		if( member.gsEmail ) arrInfos.push( member.gsEmail );

		this.matDialog.open( InfoDialogComponent, {
			width: '350px',
			data: { messages: [ arrInfos ] }
		});
	}

	showInfoSection( i: number ) {

		let description = '', designation = '';
		if( !this.selectedSectionIds.includes( '' ) ) {
			const section = this.sections.find( s => s.sparteId == this.selectedSectionIds[i] );
			description = section ? section.sparteBeshreibung : '';
			designation = section ? section.sparteBezeichnung : '';
		}
		else {
			description = this.sectionsAssigned[i].sparteBeshreibung;
			designation = this.sectionsAssigned[i].sparteBezeichnung;
		}


		this.matDialog.open( InfoDialogComponent, {
			data: { messages: [ description, designation ] }
		});

	}

	print() {
		window.print();
	}
}

